import {
  COMMISSION_EXCLUSION_LABELS,
  COMMISSION_LINE_TYPE_LABELS,
  COMMISSION_PAYMENT_LABELS,
  PARTNER_AGREEMENT_STATUS_LABELS,
  canOpenContract,
  canOpenInvoice,
  formatDate,
  formatHours,
  formatPercent,
  type CommissionPayment,
  type PartnerAgreement,
  type PartnerAgreementStatus,
  type PartnerCommissionLine,
} from '@codepass/shared';
import { useAuth } from '@/auth/AuthProvider';
import { Badge, Table, Td, Th, Tr, type BadgeTone } from '@/components/ui';
import { Money, PermittedLink } from '@/components/domain';
import { contractPath, invoicePath } from '@/lib/paths';

/**
 * The pieces partner commission is shown with - on the Partneraftale tab on /kontrakter/:id and on
 * /fakturering/provision. One table for both, so a line reads the same in the two places.
 *
 * Display only: the commission, the payment and whether a line is settled are worked out by the
 * server - see `partnerCommission` in @codepass/shared.
 */

const AGREEMENT_STATUS_TONE: Record<PartnerAgreementStatus, BadgeTone> = {
  draft: 'neutral',
  active: 'success',
  terminated: 'warning',
  ended: 'danger',
};

const PAYMENT_TONE: Record<CommissionPayment, BadgeTone> = {
  paid: 'success',
  awaiting_payment: 'warning',
  credited: 'neutral',
  partly_credited: 'warning',
  unresolved: 'neutral',
};

/** A rate as CRM has it: `15 %`, or `12,50 %` when it has decimals. Null is a missing rate. */
export function rateLabel(rate: number | null): string {
  if (rate === null) return 'Mangler';
  return formatPercent(rate, Number.isInteger(rate) ? 0 : 2);
}

/** The agreement's status as a badge - CRM's own label when Codepass does not know the status. */
export function AgreementStatusBadge({ agreement }: { agreement: PartnerAgreement | null }) {
  if (agreement?.status) {
    return (
      <Badge tone={AGREEMENT_STATUS_TONE[agreement.status]}>
        {PARTNER_AGREEMENT_STATUS_LABELS[agreement.status]}
      </Badge>
    );
  }
  return <Badge tone="neutral">{agreement?.status_label ?? 'Ukendt status'}</Badge>;
}

/** A line with the contract it belongs to - only on /fakturering/provision. */
type Line = PartnerCommissionLine & { contract_no?: string; customer_name?: string | null };

/**
 * The commission lines. With `showContract`, a column says which agreement and customer each line
 * is on - /fakturering/provision covers several; the contract tab is one.
 *
 * A settled line shows its statement under Betaling: it has been paid out, and that is what the
 * column answers.
 */
export function CommissionLinesTable({
  lines,
  currency = 'DKK',
  showContract = false,
}: {
  lines: readonly Line[];
  currency?: string;
  showContract?: boolean;
}) {
  /* A line carries the contract's number and the invoice's - not its kind or status. */
  const { user } = useAuth();
  const opensContract = canOpenContract(user, null);
  const opensInvoice = canOpenInvoice(user, null);
  return (
    <>
      <Table>
        <thead>
          <tr>
            {showContract ? <Th className="w-[180px]">Aftale</Th> : null}
            <Th className="w-[130px]">Faktura</Th>
            <Th>Linje</Th>
            <Th className="w-[140px] text-right">Beløb ekskl. moms</Th>
            <Th className="w-[80px] text-right">Sats</Th>
            <Th className="w-[150px] text-right">Provision</Th>
            <Th className="w-[160px]">Betaling</Th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <Tr key={line.line_id}>
              {showContract ? (
                <Td>
                  {line.contract_no ? (
                    <PermittedLink
                      to={contractPath({ id: line.contract_no, contract_no: line.contract_no })}
                      allowed={opensContract}
                      className="font-medium hover:underline"
                    >
                      {line.contract_no}
                    </PermittedLink>
                  ) : (
                    '—'
                  )}
                  <span className="block text-xs text-ink-muted">{line.customer_name ?? '—'}</span>
                </Td>
              ) : null}
              <Td>
                <PermittedLink
                  to={invoicePath({ id: line.invoice_id, bc_invoice_id: line.invoice_no })}
                  allowed={opensInvoice}
                  className="font-medium hover:underline"
                >
                  {line.invoice_no ?? 'Uden nummer'}
                </PermittedLink>
                <span className="block text-xs text-ink-muted">
                  {formatDate(line.invoice_date)}
                </span>
              </Td>
              <Td>
                <span>{line.description}</span>
                <span className="block text-xs text-ink-muted">
                  {COMMISSION_LINE_TYPE_LABELS[line.line_type]}
                  {line.item_no ? ` · ${line.item_no}` : ''}
                  {line.line_type === 'hours' ? ` · ${formatHours(line.quantity)} timer` : ''}
                </span>
              </Td>
              <Td className="text-right">
                <Money value={line.amount_excl_vat} currency={currency} />
              </Td>
              <Td className="text-right tabular">
                {line.rate_pct === null ? '—' : rateLabel(line.rate_pct)}
              </Td>
              <Td className="text-right">
                {line.commission_excl_vat === null ? (
                  <>
                    <span className="text-ink-muted">—</span>
                    {line.excluded ? (
                      <span className="block text-xs text-ink-muted">
                        {COMMISSION_EXCLUSION_LABELS[line.excluded]}
                      </span>
                    ) : null}
                  </>
                ) : (
                  <Money value={line.commission_excl_vat} currency={currency} />
                )}
              </Td>
              <Td>
                {line.settlement ? (
                  <>
                    <Badge tone="brand">Afregnet</Badge>
                    <span className="block text-xs text-ink-muted tabular">
                      {line.settlement.statement_no}
                    </span>
                  </>
                ) : (
                  <>
                    <Badge tone={PAYMENT_TONE[line.payment]}>
                      {COMMISSION_PAYMENT_LABELS[line.payment]}
                    </Badge>
                    {line.paid_date ? (
                      <span className="block text-xs text-ink-muted tabular">
                        {formatDate(line.paid_date)}
                      </span>
                    ) : null}
                  </>
                )}
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>
      {/*
        The ledger has no "closed at" date, so the payment date is matched from the payments
        oldest first - see `settleInvoices`. Said once, under the table, rather than on every row.
      */}
      {lines.some((line) => line.paid_date && !line.settlement) ? (
        <p className="px-4 py-3 text-xs text-ink-muted">
          Betalingsdatoen er anslået ud fra debitorposterne i Business Central.
        </p>
      ) : null}
    </>
  );
}
